import { useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Camera, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { http, errorMessage } from "@/lib/api";

// Shrink phone photos in the browser so uploads stay small and fast.
const shrink = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(img.src);
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't read that photo."))), "image/jpeg", 0.85);
  };
  img.onerror = () => reject(new Error("Couldn't read that photo. Please try another."));
  img.src = URL.createObjectURL(file);
});

export const DeliveredDialog = ({ s, open, onOpenChange, onDone, auth }) => {
  const [f, setF] = useState({ date: format(new Date(), "yyyy-MM-dd"), left_at: s.instructions || "", note: "" });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(true);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const blob = await shrink(file);
      setPhoto({ blob, url: URL.createObjectURL(blob) });
    } catch (err) {
      toast.error(err.message);
    }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("notify", notify ? "true" : "false");
      if (photo) fd.append("photo", photo.blob, "delivery.jpg");
      const { data } = await http.post(`/admin/subscriptions/${s.subscription_id}/delivered`, fd, { ...auth(), timeout: 60000 });
      toast.success(data.message);
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-[#262626] bg-[#101010] text-[#F7F5F0]" data-testid="delivered-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Mark as delivered</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">{notify ? `${s.name} will get a delivery confirmation email with the photo as proof.` : `This only records the delivery. ${s.name} won't be emailed.`}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="dl-date">Delivered on</Label>
            <Input id="dl-date" type="date" value={f.date} max={format(new Date(), "yyyy-MM-dd")} onChange={(e) => setF({ ...f, date: e.target.value })} className="lc-input [color-scheme:dark]" data-testid="delivered-date-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dl-left">Left at</Label>
            <Input id="dl-left" value={f.left_at} maxLength={200} onChange={(e) => setF({ ...f, left_at: e.target.value })} placeholder="e.g. Beside the garage" className="lc-input" data-testid="delivered-left-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dl-note">Note for customer (optional)</Label>
            <Input id="dl-note" value={f.note} maxLength={300} onChange={(e) => setF({ ...f, note: e.target.value })} className="lc-input" data-testid="delivered-note-input" />
          </div>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[#3a3a3a] p-5 text-sm text-[#A1A1AA] hover:border-[#FF6A00]" data-testid="delivered-photo-label">
            {photo ? <img src={photo.url} alt="Delivery preview" className="max-h-64 rounded-xl" data-testid="delivered-photo-preview" /> : <Camera className="h-6 w-6 text-[#D4A437]" />}
            {photo ? "Tap to change photo" : "Add delivery photo (take one or choose from your phone)"}
            <input type="file" accept="image/*" className="sr-only" onChange={pick} data-testid="delivered-photo-input" />
          </label>
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-[#262626] px-4 py-3 text-sm" data-testid="delivered-notify-label">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="h-4 w-4 accent-[#FF6A00]" data-testid="delivered-notify-checkbox" />
            Email the customer a delivery confirmation
          </label>
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={busy || !f.date} className="lc-btn-primary w-full !py-3" data-testid="delivered-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4" /> {notify ? "Confirm delivery & email customer" : "Record delivery (no email)"}</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
